import { useEffect, useState } from 'react';

import { useIsFetching, useIsMutating } from '@tanstack/react-query';

const FAST_INCREMENT = 10;
const MID_INCREMENT = 5;
const FINAL_INCREMENT = 1;

type UseGlobalLoadingIndicatorParams = {
  mode: 'progress' | 'backdrop';
};

type GlobalLoadingIndicatorState = {
  hasActiveRequests: boolean;
  isProgressMode: boolean;
  isVisible: boolean;
  progress: number;
  shouldShowProgress: boolean;
  shouldShowBackdrop: boolean;
};

export function useGlobalLoadingIndicator({ mode }: UseGlobalLoadingIndicatorParams): GlobalLoadingIndicatorState {
  // Exclui queries que têm skipGlobalLoader: true no meta
  const fetchCount = useIsFetching({
    predicate: (query) => !query.meta?.skipGlobalLoader,
  });
  const mutationCount = useIsMutating();
  const [progress, setProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  const hasActiveRequests = fetchCount > 0 || mutationCount > 0;
  const isProgress = mode === 'progress';

  useEffect(() => {
    if (mode === 'progress') {
      return;
    }

    setIsVisible(false);
    setProgress(0);
  }, [mode]);

  useEffect(() => {
    if (!isProgress || !hasActiveRequests) {
      return;
    }

    setIsVisible(true);
    setProgress(0);

    const intervalId = setInterval(() => {
      setProgress((prev) => {
        if (!hasActiveRequests || prev >= 95) {
          return prev;
        }

        return Math.min(prev + getProgressIncrement(prev), 95);
      });
    }, 200);

    return (): void => {
      clearInterval(intervalId);
    };
  }, [hasActiveRequests, isProgress]);

  useEffect(() => {
    if (!isProgress || hasActiveRequests || !isVisible) {
      return;
    }

    setProgress(100);

    const timeoutId = setTimeout(() => {
      setIsVisible(false);
      setProgress(0);
    }, 300);

    return (): void => {
      clearTimeout(timeoutId);
    };
  }, [hasActiveRequests, isVisible, isProgress]);

  return {
    hasActiveRequests,
    isProgressMode: isProgress,
    isVisible,
    progress,
    shouldShowProgress: isProgress && (isVisible || hasActiveRequests),
    shouldShowBackdrop: !isProgress && hasActiveRequests,
  };
}

function getProgressIncrement(value: number): number {
  if (value < 60) {
    return FAST_INCREMENT;
  }

  if (value < 85) {
    return MID_INCREMENT;
  }

  return FINAL_INCREMENT;
}
